<template>
  <v-app>
    <v-navigation-drawer v-model="drawer" app v-if="ready">
      <feed-banner :feed="rss" @click="openFeedInfoView()" />
      <v-divider />
      <feed-category-chooser
        :categories="categories"
        v-model="currentCategory"
        @click-item="$vuetify.breakpoint.mobile ? (drawer = false) : null"
      />
    </v-navigation-drawer>

    <v-app-bar app dark color="primary">
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title>
        {{ appBarTitle }}
      </v-toolbar-title>
      <v-spacer />
      <v-menu left bottom transition="slide-y-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-view-list</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-for="layout in newsLayoutTypes"
            :key="layout.id"
            :input-value="layout.id == newsLayout"
            @click="newsLayout = layout.id"
          >
            <v-list-item-title>{{ layout.text }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main v-scroll="onScroll">
      <news-list
        :source="newsList"
        :layout="newsLayout"
        @click-item="openNews($event)"
      />
      <v-fab-transition>
        <v-btn
          color="primary"
          dark
          fab
          fixed
          bottom
          right
          v-show="fabVisible"
          @click="scrollTop()"
        >
          <v-icon>mdi-arrow-up</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-main>

    <v-dialog
      fullscreen
      :value="detailVisible"
      transition="dialog-bottom-transition"
      scrollable
    >
      <news-view :data="currentNews" @close="closeNews()" />
    </v-dialog>
    <v-dialog
      :value="feedInfoVisible"
      scrollable
      width="800"
      @click:outside="closeFeedInfoView()"
    >
      <v-card tile>
        <v-toolbar>RSS源信息</v-toolbar>
        <v-card-text class="pa-0">
          <meta-list-view :meta="rss" :meta-desc="feedInfoMetaDesc" />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="closeFeedInfoView()">关闭</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <loading-dialog
      :loading="loading || loadFailed"
      :progress="loadProgress"
      :error="loadFailed"
      :prompt="progressTitle"
      :progress-text="progressText"
    />
  </v-app>
</template>

<script>
import LoadingDialog from "@/components/LoadingDialog";
import FeedBanner from "@/components/FeedBanner";
import FeedCategoryChooser from "@/components/FeedCategoryChooser";
import NewsList from "@/components/NewsList";
import NewsView from "@/components/NewsView";
import MetaListView from "@/components/MetaListView";
import toDateString from "@/mixins/toDateString";
import toSizeString from "@/mixins/toSizeString";
import metaDesc from "@/script/metaDesc";
import { mapState, mapGetters } from "vuex";

const newsLayoutTypes = [
  {
    id: "list",
    text: "仅展示标题",
  },
  {
    id: "snippet",
    text: "展示选段",
  },
  {
    id: "post",
    text: "展示全文",
  },
];
export default {
  components: {
    LoadingDialog,
    FeedBanner,
    FeedCategoryChooser,
    NewsList,
    NewsView,
    MetaListView,
  },
  mixins: [toDateString, toSizeString],
  data: () => ({
    drawer: true,
    newsLayout: "list",
    fabVisible: false,
    feedInfoVisible: false,
    newsLayoutTypes,
    feedInfoMetaDesc: metaDesc.feedInfo,
  }),
  computed: {
    ...mapState({
      loading: (state) => state.status == "loading",
      ready: (state) => state.status == "ready",
      loadFailed: (state) => state.status == "error",
      loadProgress: (state) =>
        (state.progressLoaded / state.progressTotal) * 100,
      rss: (state) => state.rss,
      currentNews: (state) => state.currentNews,
      detailVisible: (state) => state.detailVisible,
      appBarTitle(state) {
        var result = [],
          filter = state.filter;
        if (!filter)
          return this.$store.getters.rawRSS.title || "RSS 在线阅览器";
        if (filter.category) result.push(filter.category);
        if (filter.dateRange) {
          result.push(
            this.dateTimeToString(filter.dateRange[0]) +
              "至" +
              this.dateTimeToString(filter.dateRange[1])
          );
        }
        if (filter.title) result.push("标题包含“" + filter.title + "”");
        if (filter.titleOrContent)
          result.push("包含“" + filter.titleOrContent + "”");
        if (filter.link) result.push("链接包含“" + filter.link + "”");
        return result.join(" & ");
      },
      progressTitle(state) {
        if (state.status == "error") {
          return "拉取数据失败";
        }
        return "正在拉取数据…… ";
      },
      progressText(state) {
        if (state.status == "error") {
          return state.loadError;
        }
        let loadedSize = this.fileSizeToString(state.progressLoaded),
          speed = this.fileSizeToString(state.progressSpeed);
        if (state.progressTotal > 0) {
          let totalSize = this.fileSizeToString(state.progressTotal),
            estimateTime = this.durationToString(state.progressEstimate),
            progressPercent = (
              (state.progressLoaded / state.progressTotal) *
              100
            ).toFixed(1);
          return `${loadedSize} / ${totalSize} (${progressPercent}% ~${estimateTime})`;
        } else {
          return `${loadedSize} (${speed}/s)`;
        }
      },
    }),
    ...mapGetters(["categories", "newsList", "historyState"]),
    currentCategory: {
      get() {
        return (this.$store.state.filter || {}).category;
      },
      set(category) {
        this.$store.dispatch("setCategory", { category });
      },
    },
  },
  watch: {
    appBarTitle(newValue) {
      document.title = newValue;
    },
    historyState(newValue) {
      var url = location.origin + location.pathname;
      if (newValue.querystring) {
        url += "?" + newValue.querystring;
      }
      if (history.state != null) {
        history.pushState(newValue.state, null, url);
      } else {
        history.replaceState(newValue.state, null, url);
      }
    },
    newsLayout() {
      this.$vuetify.goTo(0, { duration: 0 });
    },
  },
  methods: {
    openNews(newsItem) {
      this.$store.dispatch("openNews", { newsItem });
    },
    closeNews() {
      this.$store.dispatch("closeNews");
    },
    restoreState(event) {
      this.$store.dispatch("restoreFromState", event.state);
    },
    openFeedInfoView() {
      this.feedInfoVisible = true;
    },
    closeFeedInfoView() {
      this.feedInfoVisible = false;
    },
    scrollTop() {
      this.$vuetify.goTo(0, {
        duration: 300,
        easing: "easeInOutCubic",
      });
    },
    onScroll() {
      var scrollTop =
        document.documentElement.scrollTop + document.body.scrollTop;
      if (scrollTop > 500) {
        this.fabVisible = true;
      } else {
        this.fabVisible = false;
      }
    },
  },
  created() {
    window.addEventListener("popstate", this.restoreState);
    this.$store.dispatch("restoreFromSearch", {
      querystring: location.search.substring(1),
    });
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.restoreState);
  },
};
</script>

<style>
* ::-webkit-scrollbar {
  background-color: #aba499;
  color: #202324;
  width: 6px;
}
* ::-webkit-scrollbar-thumb {
  background-color: #5a5e62;
}
* ::-webkit-scrollbar-thumb:hover {
  background-color: #737d81;
}
* ::-webkit-scrollbar-thumb:active {
  background-color: #363b3c;
}
* ::-webkit-scrollbar-corner {
  background-color: #222526;
}
* {
  scrollbar-color: #c2cfd6 #ddebf1;
}
</style>